import { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import axiosClient from "../../axios";

const Lessons = () => {
  const [questions, setQuestions] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [categories, setCategories] = useState([]);
  const [flatOptions, setFlatOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const flattenCategories = (nodes, level = 0) => {
    return nodes.flatMap((node) => {
      const indent = "\u00A0\u00A0".repeat(level);
      const label = `${indent}${node.name}`;
      const option = {
        label,
        value: node.id,
        disabled: node.taxonomy !== "video",
      };

      const children = node.children
        ? flattenCategories(node.children, level + 1)
        : [];

      return [option, ...children];
    });
  };

  const fetchQuestions = async () => {
    if (!selectedCategory) {
      setQuestions([]);
      setTotal(0);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/lessons`,
        {
          params: {
            page: currentPage,
            perPage,
            category: selectedCategory,
          },
        }
      );
      setQuestions(response.data.data);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching questions:", error);
      setError("Failed to load questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/getTermsExcludingTaxonomy`,
        {
          params: {
            taxonomy: "quiz",
          },
        }
      );
      setCategories(response.data);
      const flat = flattenCategories(response.data);
      setFlatOptions(flat);
    } catch (err) {
      console.error("Error fetching categories:", err);
      setError("Failed to load categories. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [selectedCategory, currentPage, perPage]);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/api/lessons/${id}`
        );
        fetchQuestions();
      } catch (error) {
        console.error("Error deleting question:", error);
        setError("Failed to delete question. Please try again.");
      }
    }
  };

  // 🔄 Drag and Drop Logic
  const handleDragStart = (e, index) => {
    e.dataTransfer.setData("dragIndex", index);
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = parseInt(e.dataTransfer.getData("dragIndex"));
    if (dragIndex === dropIndex) return;

    const reordered = [...questions];
    const [movedItem] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, movedItem);

    // Assign new positions
    const updated = reordered.map((item, idx) => ({
      ...item,
      position: idx + 1,
    }));

    setQuestions(updated);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/update-lesson-positions`,
        {
          items: updated.map(({ id, position }) => ({ id, position })),
        }
      );
    } catch (error) {
      console.error("Failed to update lesson positions", error);
    }
  };

  return (
    <div className="container">
      <div className="d-flex align-items-center justify-content-start mb-4">
        <h2 className="mb-0 me-3">Lessons</h2>
        <Link
          to="/xl-admin/add-lesson"
          className="btn btn-inchworm btn-sm"
          role="button"
          style={{
            padding: "0.25rem 0.4rem",
            fontSize: "0.8rem",
            lineHeight: "1",
          }}
        >
          Add New
        </Link>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      <div className="row mb-4">
        <div className="col-md-6 mb-2">
          <select
            className="form-select"
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">Select a Category</option>
            {flatOptions.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-3 mb-2">
          <select
            className="form-select"
            value={perPage}
            onChange={(e) => setPerPage(Number(e.target.value))}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
            <option value="100">100 per page</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div
          className="d-flex flex-column justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <ClipLoader color="#5cd400" loading={true} size={50} />
          <div className="mt-3 text-muted fs-5">Loading, please wait...</div>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-bordered table-striped">
              <thead className="table-success">
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Position</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {questions.length > 0 ? (
                  questions.map((question, index) => (
                    <tr
                      key={question.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => handleDrop(e, index)}
                      style={{ cursor: "move" }}
                    >
                      <td>{question.id}</td>
                      <td>{question.title}</td>
                      <td>{question.category}</td>
                      <td>{question.position || index + 1}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Link
                            to={`/xl-admin/edit-lesson?id=${question.id}`}
                            className="btn btn-warning btn-sm"
                          >
                            Edit
                          </Link>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(question.id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">
                      {error ? error : "Select a category with lessons"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="d-flex justify-content-center mt-4">
              <nav>
                <ul className="pagination">
                  {Array.from(
                    { length: Math.ceil(total / perPage) },
                    (_, i) => (
                      <li
                        key={i}
                        className={`page-item ${
                          i + 1 === currentPage ? "active" : ""
                        }`}
                      >
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    )
                  )}
                </ul>
              </nav>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Lessons;
